/**
 * Bills list (S14) — purchases-side bill inbox.
 *
 * @see prototype/purchases/bills.html — V1.1 baseline (S14)
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Alert, ConfirmDialog, toast } from "@flint/ui";
import { STEP_UP_ACTIONS } from "@flint/api-contract";
import { trpc } from "../../../../lib/trpc.ts";
import { paiseToInrShort } from "../../../../lib/format.ts";
import { usePermission } from "../../../../lib/permissions.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { ListPagination } from "../../../../features/master-list/index.ts";
import { useStepUpGate } from "../../../../features/auth/index.ts";
import {
  ApprovalBanner,
  BillBulkActionBar,
  BillFilterBar,
  BillKpiStrip,
  BillListHeader,
  BillStatusTabs,
  BillTable,
  downloadBillsCsv,
  downloadBillsPdf,
  downloadBillsXlsx,
  type BillAmountFilter,
  type BillDateFilter,
  type BillExportFormat,
  type BillListRow,
  type BillStatus,
  type BillStatusFilter,
} from "../../../../features/bills/list/index.ts";

export const Route = createFileRoute("/(workspace)/procurement/bills/")({
  component: BillsList,
});

/** localStorage key holding the last-dismissed approvals-banner signature. */
const APPROVAL_BANNER_DISMISSED_KEY = "flint.bills.approvalBannerDismissed";

/** Maps the operator-facing tab filter to the V1 `bill_status` set. */
const STATUSES_FOR_FILTER: Record<BillStatusFilter, BillStatus[] | null> = {
  all: null,
  draft: ["draft"],
  awaiting: ["review", "approval_pending", "ready_to_pay"],
  approved: ["ready_to_pay"],
  paid: ["paid"],
  recurring: null, // surfaced via KPI strip, not via bill_status
};

function BillsList() {
  const canCreate = usePermission("bills.create");
  const utils = trpc.useUtils();
  const [statusFilter, setStatusFilter] = useState<BillStatusFilter>("all");
  const [search, setSearch] = useState("");
  const [vendorId, setVendorId] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<BillDateFilter>("all");
  const [amountFilter, setAmountFilter] = useState<BillAmountFilter>("all");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Destructive-action targets — populated by the row-menu callbacks,
  // cleared on success or cancel. Tracking just the row gives the dialog
  // copy the vendor + total to make the consequence concrete.
  const [deleteTarget, setDeleteTarget] = useState<BillListRow | null>(null);
  const [voidTarget, setVoidTarget] = useState<BillListRow | null>(null);
  // Approvals banner is dismissable. We persist the dismissed *signature*
  // (the pending-approval counts), so the banner reappears when a new
  // approval need arises rather than being silenced forever.
  const [dismissedBannerSig, setDismissedBannerSig] = useState<string | null>(() => {
    try {
      return localStorage.getItem(APPROVAL_BANNER_DISMISSED_KEY);
    } catch {
      return null;
    }
  });

  const deleteDraft = trpc.bills.deleteDraft.useMutation({
    onSuccess: async () => {
      await utils.bills.list.invalidate();
      setDeleteTarget(null);
      toast.success("Draft deleted", {
        description: "The draft bill and its attachments have been removed.",
      });
    },
    onError: (err: { message: string }) => {
      toast.error("Couldn't delete draft", { description: err.message });
    },
  });

  const transition = trpc.bills.transitionStatus.useMutation({
    onSuccess: async () => {
      await utils.bills.list.invalidate();
      setVoidTarget(null);
      toast.success("Bill voided", {
        description: "Voided rows stay in the ledger as historical record.",
      });
    },
    onError: (err: { message: string }) => {
      toast.error("Couldn't void bill", { description: err.message });
    },
  });

  const rowActionsBusy = deleteDraft.isPending || transition.isPending;

  // ── Bulk approve — step-up-OTP gated ─────────────────────────────
  // Hook owns the OTP dialog state + the optimistic-first attempt.
  // The actual orchestrator `approveSelectedBills` is defined below,
  // after `filteredRows` is in scope.
  const stepUpGate = useStepUpGate(STEP_UP_ACTIONS.BILLS_BATCH_APPROVE);

  // Push every server-supported filter into the tRPC input. Status maps from
  // the operator-facing tab to one or many `bill_status` values; if the tab
  // matches more than one, we leave the server filter off and narrow via the
  // returned rows (a single query so the KPI/tab counts stay coherent).
  const statusList = STATUSES_FOR_FILTER[statusFilter];
  const singleStatus = statusList && statusList.length === 1 ? statusList[0] : undefined;

  // Cursor-stack pagination (the shared list pattern — see PO list). Reset the
  // stack whenever a filter that changes the server-side set flips. The amount
  // filter is client-only (narrows the current page), so it's not a dependency.
  const PAGE_SIZE = 50;
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];
  useEffect(() => {
    setCursorStack([]);
  }, [singleStatus, vendorId, search, dateFilter]);

  const list = trpc.bills.list.useQuery(
    {
      limit: PAGE_SIZE,
      ...(singleStatus ? { status: singleStatus } : {}),
      ...(vendorId ? { vendorId } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(dateFilter === "this-month" ? { dateFrom: startOfThisMonthIso() } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
    },
    { placeholderData: (prev) => prev },
  );

  // ── Export ───────────────────────────────────────────────────────
  // Honours the server-supported filters (status / vendor / date / search)
  // — the same inputs the list query uses. Client-only narrowing (amount
  // buckets, multi-status tabs) isn't part of the export contract. CSV +
  // Excel serialise client-side off `exportRows`; PDF renders server-side.
  const [exporting, setExporting] = useState<BillExportFormat | null>(null);
  const exportPdf = trpc.bills.exportPdf.useMutation();

  const handleExport = async (format: BillExportFormat) => {
    if (exporting) return;
    const filter = {
      ...(singleStatus ? { status: singleStatus } : {}),
      ...(vendorId ? { vendorId } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(dateFilter === "this-month" ? { dateFrom: startOfThisMonthIso() } : {}),
    };
    setExporting(format);
    try {
      if (format === "pdf") {
        const res = await exportPdf.mutateAsync(filter);
        downloadBillsPdf(res.pdfBase64, res.fileName);
      } else {
        const data = await utils.bills.exportRows.fetch(filter);
        if (data.rows.length === 0) {
          toast.info("No bills match this view to export.");
          return;
        }
        if (format === "csv") downloadBillsCsv(data.rows, data.generatedAt);
        else await downloadBillsXlsx(data.rows, data.generatedAt);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed. Please try again.");
    } finally {
      setExporting(null);
    }
  };

  const allRows = (list.data?.items ?? []) as BillListRow[];

  // When the tab spans multiple statuses (e.g. "awaiting" = review +
  // approval_pending + ready_to_pay), narrow client-side. Amount filter is
  // a UI affordance only; not part of the V1 server contract.
  const filteredRows = useMemo(
    () => applyClientFilters(allRows, statusList, dateFilter, amountFilter),
    [allRows, statusList, dateFilter, amountFilter],
  );

  // Approve button is disabled when no selected row is `approval_pending`
  // — that's the only status `bills.batchApprove` can transition out of.
  const hasApprovablySelected = filteredRows.some(
    (row) => selectedIds.has(row.id) && row.status === "approval_pending",
  );

  // Per-id result shape from `bills.batchApprove`. Inline-typed here
  // because the workspace-wide tRPC type bug (`Property 'bills' does
  // not exist on type 'TRPCUntypedClient<AppRouter>'`) erases the
  // inferred return type on the imperative `utils.client.bills.…`
  // call; without this we can't read `.succeeded` / `.failed` off it.
  type BatchApproveResult = {
    succeeded: string[];
    failed: Array<{ id: string; reason: string }>;
    rows: Array<{
      id: string;
      status: string | null;
      failureReason: string | null;
    }>;
  };

  const approveSelectedBills = async () => {
    const eligible = filteredRows.filter(
      (row) => selectedIds.has(row.id) && row.status === "approval_pending",
    );
    if (eligible.length === 0) {
      toast.error("No approval-pending bills selected", {
        description: "Only bills awaiting founder approval can be approved here.",
      });
      return;
    }

    const eligibleIds = eligible.map((row) => row.id);
    const totalPaise = eligible.reduce((sum, row) => sum + BigInt(row.totalPaise), 0n);

    const result = await stepUpGate.execute<BatchApproveResult>(
      () =>
        utils.client.bills.batchApprove.mutate({
          billIds: eligibleIds,
        }) as Promise<BatchApproveResult>,
      {
        title: `Approve ${eligible.length} bill${eligible.length === 1 ? "" : "s"} · ${paiseToInrShort(totalPaise.toString())}`,
        description:
          'Founder approval moves these bills to "ready to pay". Verify with the 6-digit code sent to your registered phone.',
      },
    );

    // Operator cancelled the OTP dialog.
    if (!result) return;

    await utils.bills.list.invalidate();

    const { succeeded, failed } = result;
    if (failed.length === 0) {
      setSelectedIds(new Set());
      toast.success(`Approved ${succeeded.length} bill${succeeded.length === 1 ? "" : "s"}`, {
        description: "Founder approval recorded · ready to pay.",
      });
    } else if (succeeded.length === 0) {
      toast.error("Couldn't approve any bills", {
        description: failed[0]?.reason ?? `All ${failed.length} failed — check the audit log.`,
      });
    } else {
      // Keep failed rows selected so the operator can retry / inspect.
      setSelectedIds(new Set(failed.map((f) => f.id)));
      toast.error(`Approved ${succeeded.length}, ${failed.length} failed`, {
        description: "Successful rows have moved to ready-to-pay; failed rows remain selected.",
      });
    }
  };

  const vendorOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of allRows) seen.set(row.vendorId, row.vendorName);
    return Array.from(seen, ([id, name]) => ({ id, name })).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [allRows]);

  const counts = {
    all: list.data?.counts.all ?? 0,
    draft: list.data?.counts.draft ?? 0,
    awaiting:
      (list.data?.counts.review ?? 0) +
      (list.data?.counts.approval_pending ?? 0) +
      (list.data?.counts.ready_to_pay ?? 0),
    approved: list.data?.counts.ready_to_pay ?? 0,
    paid: list.data?.counts.paid ?? 0,
    recurring: list.data?.kpis.recurringVendorCount ?? 0,
  };
  const kpis = list.data?.kpis ?? emptyKpis();

  // Dismissable approvals banner — keyed by the pending-count signature so
  // a fresh approval need re-surfaces it after a prior dismissal.
  const approvalBannerSig = `${kpis.approvalReadyCount}:${kpis.heldForProofCount}`;
  const showApprovalBanner = dismissedBannerSig !== approvalBannerSig;
  const dismissApprovalBanner = () => {
    setDismissedBannerSig(approvalBannerSig);
    try {
      localStorage.setItem(APPROVAL_BANNER_DISMISSED_KEY, approvalBannerSig);
    } catch {
      // Non-fatal — dismissal just won't persist across reloads.
    }
  };

  const lead = list.data
    ? `${list.data.counts.all} total · ${paiseToInrShort(kpis.outstandingPaise)} outstanding · ${counts.awaiting} awaiting payment · ${paiseToInrShort(kpis.dueThisWeekPaise)} due this week`
    : null;

  // Pagination derivations — page index = cursor-stack depth + 1. Computed
  // against the server-paged `allRows` (before the client status/amount/date
  // narrowing), so the "Showing X–Y" caption tracks the fetched page.
  const pageNumber = cursorStack.length + 1;
  const showingFrom = allRows.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + allRows.length;
  const totalRows = list.data ? countForStatus(statusFilter, counts) : undefined;
  const handlePrev = () => setCursorStack((s) => s.slice(0, -1));
  const handleNext = () => {
    const next = list.data?.nextCursor;
    if (next) setCursorStack((s) => [...s, next]);
  };

  // Footer 1 — the visible-set stats strip (was BillTable's inline footer).
  const billFooter =
    filteredRows.length > 0
      ? [
          { label: "Avg bill", value: paiseToInrShort(kpis.averageBillPaise) },
          { label: "Avg pay time", value: "—" },
          {
            label: "Top vendor",
            value: kpis.topVendorName
              ? `${kpis.topVendorName} · ${kpis.topVendorBillCount} bills`
              : "—",
          },
          { label: "Salary cycle", value: "Not tracked" },
          {
            label: "Recurring",
            value: `${kpis.recurringVendorCount} monthly · ${paiseToInrShort(kpis.recurringMonthlyPaise)}`,
          },
        ]
      : [];

  const onStatusChange = (next: BillStatusFilter) => {
    setStatusFilter(next);
    setSelectedIds(new Set());
  };

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Procurement" }, { label: "Bills" }]}>
      <div className="flex flex-col gap-4">
        <BillListHeader lead={lead} canCreate={canCreate} />

        {list.error ? (
          <Alert intent="bad" title="Couldn't load bills" description={list.error.message} />
        ) : null}

        {showApprovalBanner ? (
          <ApprovalBanner
            approvalReadyCount={kpis.approvalReadyCount}
            heldForProofCount={kpis.heldForProofCount}
            totalPaise={kpis.outstandingPaise}
            topVendorName={kpis.topVendorName}
            onDismiss={dismissApprovalBanner}
          />
        ) : null}

        <BillKpiStrip kpis={kpis} />

        <BillStatusTabs value={statusFilter} counts={counts} onChange={onStatusChange} />

        <BillFilterBar
          search={search}
          onSearchChange={(next) => {
            setSearch(next);
            setSelectedIds(new Set());
          }}
          vendorId={vendorId}
          vendorOptions={vendorOptions}
          onVendorIdChange={(next) => {
            setVendorId(next);
            setSelectedIds(new Set());
          }}
          dateFilter={dateFilter}
          onDateFilterChange={(next) => {
            setDateFilter(next);
            setSelectedIds(new Set());
          }}
          amountFilter={amountFilter}
          onAmountFilterChange={(next) => {
            setAmountFilter(next);
            setSelectedIds(new Set());
          }}
          onExport={(format) => void handleExport(format)}
          exporting={exporting}
        />

        <BillBulkActionBar
          selectedCount={selectedIds.size}
          onClear={() => setSelectedIds(new Set())}
          onApprove={() => void approveSelectedBills()}
          approveDisabled={!hasApprovablySelected}
          approving={stepUpGate.busy}
        />
        {/* Step-up OTP dialog — opens automatically when the server
         *  reports `step-up-required` on the optimistic batchApprove
         *  call. Single-mount per route keeps the chrome consistent. */}
        {stepUpGate.dialog}

        {list.isLoading ? (
          <p className="text-sm text-muted">Loading bills…</p>
        ) : (
          <BillTable
            rows={filteredRows}
            selectedIds={selectedIds}
            footer={billFooter}
            pagination={
              filteredRows.length > 0 ? (
                <ListPagination
                  pageNumber={pageNumber}
                  showingFrom={showingFrom}
                  showingTo={showingTo}
                  {...(typeof totalRows === "number" ? { total: totalRows } : {})}
                  sortLabel="sorted by received date"
                  hasPrev={cursorStack.length > 0}
                  hasNext={!!list.data?.nextCursor}
                  onPrev={handlePrev}
                  onNext={handleNext}
                />
              ) : undefined
            }
            busy={rowActionsBusy}
            onDeleteDraft={(row) => setDeleteTarget(row)}
            onVoidBill={(row) => setVoidTarget(row)}
            onToggleSelected={(id) =>
              setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
            onToggleSelectAll={(checked) => {
              setSelectedIds(checked ? new Set(filteredRows.map((row) => row.id)) : new Set());
            }}
          />
        )}

        <ConfirmDialog
          open={deleteTarget !== null}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
          title="Delete this draft?"
          description={
            deleteTarget
              ? `The draft bill from ${deleteTarget.vendorName} (${paiseToInrShort(deleteTarget.totalPaise)}), its line items, and any attached files will be removed. Drafts never reach the ledger — there's nothing to reverse.`
              : ""
          }
          intent="danger"
          confirmLabel={deleteDraft.isPending ? "Deleting…" : "Delete draft"}
          onConfirm={() => {
            if (deleteTarget) deleteDraft.mutate({ id: deleteTarget.id });
          }}
        />

        <ConfirmDialog
          open={voidTarget !== null}
          onOpenChange={(open) => {
            if (!open) setVoidTarget(null);
          }}
          title="Void this bill?"
          description={
            voidTarget
              ? `${voidTarget.vendorDocNumber ?? "This bill"} from ${voidTarget.vendorName} (${paiseToInrShort(voidTarget.totalPaise)}) will be voided. The row stays as a historical record and no further payment actions can be taken against it.`
              : ""
          }
          intent="danger"
          confirmLabel={transition.isPending ? "Voiding…" : "Void bill"}
          onConfirm={() => {
            if (voidTarget) transition.mutate({ id: voidTarget.id, status: "void" });
          }}
        />
      </div>
    </WorkspaceShell>
  );
}

function applyClientFilters(
  rows: BillListRow[],
  statusList: BillStatus[] | null,
  dateFilter: BillDateFilter,
  amountFilter: BillAmountFilter,
): BillListRow[] {
  return rows.filter((row) => {
    // Status narrowing — only when the operator's tab spans multiple
    // server-side statuses. Single-status tabs already filter at the server.
    if (statusList && statusList.length > 1 && !statusList.includes(row.status)) {
      return false;
    }
    if (!matchesDate(row, dateFilter)) return false;
    if (!matchesAmount(row, amountFilter)) return false;
    return true;
  });
}

function matchesDate(row: BillListRow, dateFilter: BillDateFilter): boolean {
  if (dateFilter === "all" || dateFilter === "this-month") return true;
  if (!row.dueOn) return false;
  const days = daysFromToday(row.dueOn);
  if (dateFilter === "this-week") return days >= 0 && days < 7;
  return days < 0 && row.status !== "paid" && row.status !== "void";
}

function matchesAmount(row: BillListRow, amountFilter: BillAmountFilter): boolean {
  if (amountFilter === "all") return true;
  const oneLakhPaise = 100_000_00n;
  const amount = BigInt(row.totalPaise);
  if (amountFilter === "above-one-lakh") return amount >= oneLakhPaise;
  return amount < oneLakhPaise;
}

function countForStatus(
  status: BillStatusFilter,
  counts: Record<BillStatusFilter, number>,
): number {
  return counts[status];
}

/**
 * App-level UI helpers — read wall-clock to drive the "this month" /
 * "this week" / "overdue" filters. Acceptable in app code (AGENTS.md §7
 * rule 12 applies strictly to domain code; UI date filters are not part
 * of the financial-truth surface).
 */
function startOfThisMonthIso(): string {
  const date = new Date();
  date.setUTCDate(1);
  return date.toISOString().slice(0, 10);
}

function daysFromToday(isoDate: string): number {
  const due = new Date(`${isoDate}T00:00:00Z`);
  const today = new Date();
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.round((due.getTime() - todayUtc) / (1000 * 60 * 60 * 24));
}

function emptyKpis() {
  return {
    outstandingPaise: "0",
    outstandingBillCount: 0,
    outstandingVendorCount: 0,
    awaitingApprovalPaise: "0",
    approvalReadyCount: 0,
    heldForProofCount: 0,
    paidThisMonthPaise: "0",
    paidThisMonthCount: 0,
    dueThisWeekPaise: "0",
    dueThisWeekCount: 0,
    recurringVendorCount: 0,
    recurringMonthlyPaise: "0",
    averageBillPaise: "0",
    topVendorName: null,
    topVendorBillCount: 0,
  };
}
